import { EXAM_EVENT_EXAMINEE_DATA_TEST_ID } from '@e2e/constants/exam-center';
import { useConfig } from '@exam-center/configuration';
import { useExamEventDetails } from '@exam-center/exam-event-management';
import {
  GetExamEventByIdDocument,
  GetExamEventExamineeCountSummaryDocument,
  RegistrationStatus,
  RegistrationType,
  SearchExamEventExamineesDocument,
  type SyncExamEventExamineeWithWrMutation,
  useSyncExamEventExamineeWithWrMutation,
} from '@exam-center/graphql';
import { APP_PERMISSIONS } from '@exam-center/permission';
import { Button, FileDownloadOutlinedIcon, type ButtonProps } from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { WithPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { useState } from 'react';

import {
  ImportExamineesFromWRModal,
  ImportExamineesFromWrResultModal,
  WRExamineesImportErrorModal,
} from '../../components';
import { ImportExamineesViaFileModal } from '../import-examinees-via-file-modal';

import {
  getDuplicateIdNumbersFromError,
  getExamineeCountAndSeatFromError,
  getImportFromWRErrorCode,
  getWRResultModalTitleAndDescription,
  groupExamineeValidationErrorsIntoImportExamineeErrorCards,
} from './utils';

type SyncExamEventExamineeWithWrOutput =
  SyncExamEventExamineeWithWrMutation['ecSyncExamEventExamineeWithWr'];

const IMPORTABLE_FROM_FILE_REGISTRATION_STATUSES = [
  RegistrationStatus.OpenForRegistration,
  RegistrationStatus.TemporarilyClosed,
];

const IMPORTABLE_FROM_WR_REGISTRATION_STATUSES = [
  RegistrationStatus.OpenForRegistration,
  RegistrationStatus.TemporarilyClosed,
  RegistrationStatus.SessionFull, // allow import from WR by updating only exist exam event examinee and ignore new examinee from WR
];

function isImportAllowed(registrationStatus: RegistrationStatus, isExamEventPublic: boolean) {
  if (isExamEventPublic)
    return IMPORTABLE_FROM_WR_REGISTRATION_STATUSES.includes(registrationStatus);
  return IMPORTABLE_FROM_FILE_REGISTRATION_STATUSES.includes(registrationStatus);
}

export default function ImportExamineesButton(props: Readonly<ButtonProps>) {
  const { examEvent } = useExamEventDetails();
  const [isImportViaModalOpen, importViaModalHandler] = useDisclosure();
  const [isImportFromWRModalOpen, importFromWRModalHandler] = useDisclosure();
  const [isExceedSeatNumberModalOpen, exceedSeatNumberModalHandler] = useDisclosure();
  const [isDuplicatedIdNumberModalOpen, duplicatedIdNumberModalHandler] = useDisclosure();
  const [isWrResultModalOpen, wrResultModalHandler] = useDisclosure();
  const [exceedSeatNumberError, setExceedSeatNumberError] = useState<{
    wrExamineeCount: number;
    examEventSeat: number;
  } | null>(null);
  const [duplicatedIdNumbers, setDuplicatedIdNumbers] = useState<string[]>([]);
  const configuration = useConfig();
  const { showSnackbar } = useSnackbar();
  const { importTemplateUrl } = configuration.examinee;
  const isExamEventPublic = examEvent.registrationType === RegistrationType.Public;
  const [wrSyncResult, setWrSyncResult] = useState<SyncExamEventExamineeWithWrOutput | null>(null);

  const [syncExamineeWithWr] = useSyncExamEventExamineeWithWrMutation({
    onCompleted: (data) => {
      importFromWRModalHandler.close();
      wrResultModalHandler.open();
      setWrSyncResult(data.ecSyncExamEventExamineeWithWr);
    },
    onError: (error) => {
      importFromWRModalHandler.close();

      const errorCode = getImportFromWRErrorCode(error);

      switch (errorCode) {
        case 'RECEIVED_EXAMINEES_EXCEEDS_EXAM_EVENT_SEAT':
          setExceedSeatNumberError(getExamineeCountAndSeatFromError(error));
          exceedSeatNumberModalHandler.open();
          break;
        case 'DUPLICATE_EXAMINEE_ID_NUMBER':
          duplicatedIdNumberModalHandler.open();
          setDuplicatedIdNumbers(getDuplicateIdNumbersFromError(error));
          break;
        case 'CANNOT_CONNECT_TO_WR':
          showSnackbar({
            message: 'ไม่สามารถเชื่อมต่อกับระบบ WR ได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง',
            variant: 'error',
          });
          break;
        case 'NO_EXAMINEE_FOUND':
          showSnackbar({
            message: 'ยังไม่มีรายชื่อผู้มีสิทธิ์สอบในรอบนี้',
            variant: 'error',
          });
          break;
        case 'EXAM_EVENT_CLOSE_REGISTRATION':
          showSnackbar({
            message: 'ไม่สามารถนำเข้ารายชื่อได้ เนื่องจากรอบสอบนี้ปิดรับสมัครแล้ว',
            variant: 'error',
          });
          break;
        default:
          showSnackbar({
            message: 'ไม่สามารถนำเข้ารายชื่อผู้มีสิทธิ์สอบได้ กรุณาลองใหม่อีกครั้ง',
            variant: 'error',
          });
      }
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      SearchExamEventExamineesDocument,
      {
        query: GetExamEventByIdDocument,
        variables: {
          getExamEventByIdInput: {
            id: examEvent.id,
          },
        },
      },
      {
        query: GetExamEventExamineeCountSummaryDocument,
        variables: {
          getExamEventExamineeCountSummaryInput: {
            examEventId: examEvent.id,
          },
        },
      },
    ],
  });
  const handleImportFromViaFile = () => {
    importViaModalHandler.open();
  };

  const handleImportFromWR = () => {
    importFromWRModalHandler.open();
    syncExamineeWithWr({
      variables: {
        syncExamEventExamineeWithWrInput: {
          examEventId: examEvent.id,
        },
      },
    });
  };

  const handleCloseWrResultModal = () => {
    wrResultModalHandler.close();
  };

  if (!isImportAllowed(examEvent.registrationStatus, isExamEventPublic)) {
    return null;
  }

  const errorCards = wrSyncResult
    ? groupExamineeValidationErrorsIntoImportExamineeErrorCards(wrSyncResult.errors ?? [])
    : [];
  const hasErrors = errorCards.length > 0;
  const validExamineeCount = wrSyncResult?.summary.validExamineeCount ?? 0;
  const canceledExamineeCount = wrSyncResult?.summary.canceledExamineeCount ?? 0;
  const hasSummary = validExamineeCount + canceledExamineeCount > 0;

  const { title: wrResultModalTitle, description: wrResultModalDescription } =
    getWRResultModalTitleAndDescription(hasErrors, hasSummary);

  return (
    <WithPermission requiredPermission={APP_PERMISSIONS.EXAM_EVENT_MANAGEMENT.EXAMINEES.UPDATE}>
      <Button
        {...props}
        data-testid={EXAM_EVENT_EXAMINEE_DATA_TEST_ID.IMPORT_BUTTON}
        onClick={isExamEventPublic ? handleImportFromWR : handleImportFromViaFile}
        startIcon={<FileDownloadOutlinedIcon />}
      >
        {isExamEventPublic ? 'Import from WR' : 'Import via file'}
      </Button>
      {isImportViaModalOpen ? (
        <ImportExamineesViaFileModal
          open={isImportViaModalOpen}
          onClose={importViaModalHandler.close}
          examEventId={examEvent.id}
          importTemplateUrl={importTemplateUrl}
        />
      ) : null}
      <ImportExamineesFromWRModal open={isImportFromWRModalOpen} />
      <WRExamineesImportErrorModal
        variant='exceedSeatNumber'
        open={isExceedSeatNumberModalOpen}
        onClose={exceedSeatNumberModalHandler.close}
        wrExamineeCount={exceedSeatNumberError?.wrExamineeCount ?? 0}
        examEventSeat={exceedSeatNumberError?.examEventSeat ?? 0}
      />
      <WRExamineesImportErrorModal
        variant='duplicatedIdNumber'
        open={isDuplicatedIdNumberModalOpen}
        onClose={duplicatedIdNumberModalHandler.close}
        duplicateIdNumbers={duplicatedIdNumbers}
      />

      <ImportExamineesFromWrResultModal
        open={isWrResultModalOpen}
        title={wrResultModalTitle}
        description={wrResultModalDescription}
        summary={
          hasSummary
            ? {
                total: validExamineeCount + canceledExamineeCount,
                ready: validExamineeCount,
                canceled: canceledExamineeCount,
              }
            : undefined
        }
        errors={hasErrors ? errorCards : undefined}
        maxWidth={hasErrors ? 'sm' : 'xs'}
        onClose={handleCloseWrResultModal}
        slotProps={{
          transition: {
            onExited: () => {
              setWrSyncResult(null);
            },
          },
        }}
      />
    </WithPermission>
  );
}
